import { useEffect, useMemo, useRef, useState } from "react";
import type { ClipboardEvent, ReactNode } from "react";
import { formatTotpCode, isValidTotp, parseTotp, totpState } from "@passwordflow/crypto";
import type { TotpParams } from "@passwordflow/crypto";
import { ClipboardPaste, Copy, ImageUp } from "lucide-react";
import { QrError, decodeQrImage, pastedImage, readClipboardImage } from "../../lib/qr.js";
import { Field, SecretInput } from "../../ui/forms.js";
import { Button, IconButton } from "../../ui/primitives.js";
import { PAYLOAD_LIMITS } from "../../vaultPayload.js";

function tryParse(input: string): TotpParams | undefined {
  try {
    return parseTotp(input);
  } catch {
    return undefined;
  }
}

/** Re-renders every second while mounted. */
function useSecondTick(): number {
  const [now, setNow] = useState(Date.now());
  useEffect((): (() => void) => {
    const id = window.setInterval((): void => setNow(Date.now()), 1000);
    return (): void => window.clearInterval(id);
  }, []);
  return now;
}

function Countdown(props: { readonly remaining: number; readonly period: number }): ReactNode {
  const r = 9;
  const circumference = 2 * Math.PI * r;
  const fraction = props.remaining / props.period;
  const urgent = props.remaining <= 5;
  return (
    <span className="relative inline-grid size-7 shrink-0 place-items-center" aria-label={`Noch ${props.remaining} Sekunden gültig`} role="img">
      <svg viewBox="0 0 24 24" className="absolute inset-0 -rotate-90" aria-hidden="true">
        <circle cx="12" cy="12" r={r} fill="none" strokeWidth="2.5" className="stroke-line" />
        <circle
          cx="12"
          cy="12"
          r={r}
          fill="none"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - fraction)}
          className={urgent ? "stroke-buoy" : "stroke-flow"}
        />
      </svg>
      <span className={`text-[0.625rem] font-semibold tabular-nums ${urgent ? "text-buoy" : "text-mist"}`} aria-hidden="true">
        {props.remaining}
      </span>
    </span>
  );
}

export interface TotpCodeProps {
  readonly totp: string;
  readonly onCopy: (code: string) => void;
}

/** Live code with countdown for the entry view. */
export function TotpCode(props: TotpCodeProps): ReactNode {
  const now = useSecondTick();
  const params = useMemo((): TotpParams | undefined => tryParse(props.totp), [props.totp]);
  if (params === undefined) {
    return <span className="text-buoy">Ungültiger 2FA-Schlüssel – bitte im Eintrag korrigieren.</span>;
  }
  const state = totpState(params, now);
  return (
    <div className="flex items-center gap-3">
      <span className="font-mono text-lg font-semibold tracking-[0.08em] text-foam tabular-nums" data-totp-code={state.code}>
        {formatTotpCode(state.code)}
      </span>
      <Countdown remaining={state.remaining} period={state.period} />
      <IconButton label="2FA-Code kopieren" icon={Copy} size="sm" onClick={(): void => props.onCopy(totpState(params, Date.now()).code)} />
    </div>
  );
}

export function totpSummary(input: string): string | undefined {
  const params = tryParse(input);
  if (params === undefined) {
    return undefined;
  }
  const who = [params.issuer, params.account].filter((s: string): boolean => s !== "").join(" · ");
  const shape = `${params.digits} Ziffern · ${params.period} s · ${params.algorithm.replace("SHA", "SHA-")}`;
  return who !== "" ? `${who} – ${shape}` : shape;
}

export interface TotpFieldProps {
  readonly value: string;
  readonly error?: string | undefined;
  readonly onChange: (value: string) => void;
}

/** Editor field: otpauth link or base32 secret, or a QR code from an image or the clipboard. */
export function TotpField(props: TotpFieldProps): ReactNode {
  const fileRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const { onChange } = props;
  const trimmed = props.value.trim();
  const summary = trimmed !== "" ? totpSummary(trimmed) : undefined;

  async function importFrom(source: () => Promise<Blob>): Promise<void> {
    setBusy(true);
    setImportError(undefined);
    try {
      const text = await decodeQrImage(await source());
      if (!isValidTotp(text)) {
        setImportError("Der QR-Code enthält keinen gültigen 2FA-Schlüssel (otpauth://totp/…).");
        return;
      }
      onChange(text);
    } catch (error: unknown) {
      setImportError(error instanceof QrError ? error.message : "Der QR-Code konnte nicht gelesen werden.");
    } finally {
      setBusy(false);
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>): void {
    const image = pastedImage(event.clipboardData);
    if (image !== undefined) {
      event.preventDefault();
      void importFrom(async (): Promise<Blob> => image);
    }
  }

  const error = importError ?? props.error;
  return (
    <Field
      id="item-totp"
      label="2FA-Schlüssel (TOTP)"
      hint={error === undefined ? summary ?? "otpauth-Link oder Base32-Geheimnis. QR-Code als Bild laden oder mit Strg + V einfügen." : undefined}
      error={error}
    >
      <SecretInput
        id="item-totp"
        value={props.value}
        maxLength={PAYLOAD_LIMITS.totp}
        autoComplete="off"
        revealLabel="2FA-Schlüssel"
        invalid={error !== undefined}
        onPaste={onPaste}
        onChange={(event): void => {
          setImportError(undefined);
          onChange(event.target.value);
        }}
      />
      <div className="flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" icon={ImageUp} loading={busy} onClick={(): void => fileRef.current?.click()}>
          QR-Code aus Bild
        </Button>
        <Button
          variant="ghost"
          size="sm"
          icon={ClipboardPaste}
          disabled={busy}
          onClick={(): void => void importFrom(readClipboardImage)}
        >
          QR-Code aus Zwischenablage
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          aria-label="QR-Code-Bild wählen"
          data-testid="totp-qr-file"
          onChange={(event): void => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file !== undefined) {
              void importFrom(async (): Promise<Blob> => file);
            }
          }}
        />
      </div>
    </Field>
  );
}
